/** Lightweight SEO helper — set document title, meta description, OG tags,
 * canonical link, and a JSON-LD blob for structured data. No new deps.
 *
 * Usage:
 *   useSeo({
 *     title: "Train Driver jobs UK | Railway Careers",
 *     description: "Live UK rail vacancies …",
 *     canonical: "/careers/train-driver",
 *     image: "/og/train-driver.jpg",
 *     jsonLd: { "@context": "https://schema.org", "@type": "JobPosting", … },
 *   });
 */
import { useEffect } from "react";

const SITE_NAME = "Railway Careers UK";

function ensureMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.create).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content);
}

export function useSeo({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
  jsonLdId = "rc-jsonld",
  noindex = false,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      ensureMeta('meta[name="description"]', {
        create: { name: "description" },
        content: description,
      });
      ensureMeta('meta[property="og:description"]', {
        create: { property: "og:description" },
        content: description,
      });
      ensureMeta('meta[name="twitter:description"]', {
        create: { name: "twitter:description" },
        content: description,
      });
    }

    if (title) {
      ensureMeta('meta[property="og:title"]', {
        create: { property: "og:title" },
        content: title,
      });
      ensureMeta('meta[name="twitter:title"]', {
        create: { name: "twitter:title" },
        content: title,
      });
    }

    ensureMeta('meta[property="og:type"]', { create: { property: "og:type" }, content: type });
    ensureMeta('meta[property="og:site_name"]', { create: { property: "og:site_name" }, content: SITE_NAME });
    ensureMeta('meta[name="twitter:card"]', { create: { name: "twitter:card" }, content: "summary_large_image" });

    if (image) {
      const url = image.startsWith("http") ? image : window.location.origin + image;
      ensureMeta('meta[property="og:image"]', { create: { property: "og:image" }, content: url });
      ensureMeta('meta[name="twitter:image"]', { create: { name: "twitter:image" }, content: url });
    }

    if (canonical) {
      const href = canonical.startsWith("http") ? canonical : window.location.origin + canonical;
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
      ensureMeta('meta[property="og:url"]', { create: { property: "og:url" }, content: href });
    }

    ensureMeta('meta[name="robots"]', {
      create: { name: "robots" },
      content: noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large",
    });

    if (jsonLd) {
      let s = document.getElementById(jsonLdId);
      if (!s) {
        s = document.createElement("script");
        s.type = "application/ld+json";
        s.id = jsonLdId;
        document.head.appendChild(s);
      }
      s.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      // Don't tear meta down — next page will overwrite. Only strip JSON-LD
      // so we don't leak schemas across pages.
      if (jsonLd) {
        const s = document.getElementById(jsonLdId);
        if (s) s.remove();
      }
    };
  }, [title, description, canonical, image, type, jsonLd, jsonLdId, noindex]);
}
